import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, UserIcon, MailIcon, PhoneIcon, RocketIcon, CodeIcon, XCircleIcon, ThumbsUpIcon, ThumbsDownIcon, CheckCircleIcon } from 'lucide-react';
import { ThankYouPage } from './ThankYouPage';
type FormData = {
  englishLevel: string;
  programmingSkills: string;
  fullTime: string;
  fullName: string;
  email: string;
  phone: string;
};
type ApplicationFormProps = {
  isOpen: boolean;
  onClose: () => void;
  position?: 'programmer' | 'technical-assistant';
};
const englishLevels = [{
  id: 'basic',
  label: 'Basic',
  fill: 1
}, {
  id: 'intermediate',
  label: 'Intermediate',
  fill: 2
}, {
  id: 'advanced',
  label: 'Advanced',
  fill: 3
}, {
  id: 'fluent',
  label: 'Fluent',
  fill: 4
}, {
  id: 'proficient',
  label: 'Proficient',
  fill: 5
}];
const programmingOptions = [{
  id: 'pro',
  title: "I'm a pro",
  description: 'I have 1+ year of experience with JS and Python',
  icon: RocketIcon
}, {
  id: 'beginner',
  title: "I'm a beginner",
  description: 'I have less than 3 months of experience with JS and Python',
  icon: CodeIcon
}, {
  id: 'none',
  title: 'No experience',
  description: 'I have no experience with JS or Python',
  icon: XCircleIcon
}];
function TriangleIcon({
  fill
}: {
  fill: number;
}) {
  const fillPercentages = [0, 20, 40, 60, 80, 100];
  const fillPercent = fillPercentages[fill] || 0;
  return <svg width="32" height="32" viewBox="0 0 32 32" fill="none" className="flex-shrink-0" data-id="element-113">
      <path d="M4 28L4 4L28 28L4 28Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" fill="none" opacity="0.3" data-id="element-114" />
      <defs data-id="element-115">
        <clipPath id={`clip-${fill}`} data-id="element-116">
          <rect x="0" y={28 - 24 * fillPercent / 100} width="32" height={24 * fillPercent / 100} data-id="element-117" />
        </clipPath>
      </defs>
      <path d="M4 28L4 4L28 28L4 28Z" fill="currentColor" clipPath={`url(#clip-${fill})`} data-id="element-118" />
    </svg>;
}
function validatePhoneNumber(phone: string): boolean {
  const cleanPhone = phone.replace(/[\s-]/g, '');
  const phoneRegex = /^\+\d{10,}$/;
  return phoneRegex.test(cleanPhone);
}
// Extract UTM parameters from URL
function getUTMParameters() {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || '',
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || '',
    utm_term: params.get('utm_term') || '',
    utm_content: params.get('utm_content') || ''
  };
}
// Map programming skills to readable format
function mapProgrammingSkills(skill: string): string {
  const mapping: Record<string, string> = {
    pro: "I'm a pro",
    beginner: "I'm a beginner",
    none: 'no experience'
  };
  return mapping[skill] || skill;
}
// Generate unique ID
function generateUniqueId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
}
// Send form data to webhook
async function submitToWebhook(formData: FormData, position: 'programmer' | 'technical-assistant') {
  const utmParams = getUTMParameters();
  // Select webhook URL based on position
  const webhookUrl = position === 'technical-assistant' ? 'https://n8n.famouschat.ai/webhook/236fb782-5c53-4f38-abea-a0dc780b05a0' : 'https://n8n.famouschat.ai/webhook/efb89a9a-9fa7-495b-a3e7-53185ce94990';
  const payload = {
    payload: {
      name: position === 'technical-assistant' ? 'Technical Assistant Recruiting' : 'Programmer Recruiting',
      siteId: '62b4061307e65ff696cdc69b',
      data: {
        'Do you speak english?': formData.englishLevel,
        'Programming experience': mapProgrammingSkills(formData.programmingSkills),
        'open for full time': formData.fullTime,
        Name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        utm_source: utmParams.utm_source,
        utm_medium: utmParams.utm_medium,
        utm_campaign: utmParams.utm_campaign,
        utm_term: utmParams.utm_term,
        utm_content: utmParams.utm_content
      },
      submittedAt: new Date().toISOString(),
      id: generateUniqueId(),
      formId: '65b24090534026853e40e9c6',
      formElementId: '9ba71c4c-ffe9-61e9-3dcd-85af7d669b57',
      pageId: '65a00c538f4328ad845603d1',
      publishedPath: position === 'technical-assistant' ? '/technical-assistant/recruiting' : '/programmer/recruiting',
      schema: []
    }
  };
  const response = await fetch(webhookUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });
  if (!response.ok) {
    throw new Error('Failed to submit form');
  }
  return response;
}
export function ApplicationForm({
  isOpen,
  onClose,
  position = 'programmer'
}: ApplicationFormProps) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    englishLevel: '',
    programmingSkills: '',
    fullTime: '',
    fullName: '',
    email: '',
    phone: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // Calculate total steps based on position
  const totalSteps = position === 'technical-assistant' ? 3 : 4;
  // Map display step to actual step (skip programming for technical assistant)
  const getActualStep = (displayStep: number) => {
    if (position === 'technical-assistant' && displayStep >= 2) {
      return displayStep + 1; // Skip step 2 (programming)
    }
    return displayStep;
  };
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  const canContinue = () => {
    const actualStep = getActualStep(step);
    switch (actualStep) {
      case 1:
        return formData.englishLevel !== '';
      case 2:
        return formData.programmingSkills !== '';
      case 3:
        return formData.fullTime !== '';
      case 4:
        {
          const isPhoneValid = validatePhoneNumber(formData.phone);
          return formData.fullName !== '' && formData.email !== '' && formData.phone !== '' && isPhoneValid;
        }
      default:
        return false;
    }
  };
  const handleNext = async () => {
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      // Validate phone before submission
      if (!validatePhoneNumber(formData.phone)) {
        setPhoneError('Please enter a valid phone number (e.g., +49123456789)');
        return;
      }
      // Submit form to webhook
      setIsSubmitting(true);
      setSubmitError('');
      try {
        await submitToWebhook(formData, position);
        console.log('Form submitted successfully:', formData);
        // Dispatch custom event to notify App.tsx with position information
        window.dispatchEvent(new CustomEvent('applicationSubmitted', {
          detail: {
            position
          }
        }));
        // Close the form modal
        onClose();
      } catch (error) {
        console.error('Error submitting form:', error);
        setSubmitError('Failed to submit application. Please try again.');
        setIsSubmitting(false);
      }
    }
  };
  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    } else {
      onClose();
    }
  };
  const handleClose = () => {
    setStep(1);
    setFormData({
      englishLevel: '',
      programmingSkills: '',
      fullTime: '',
      fullName: '',
      email: '',
      phone: ''
    });
    setSubmitted(false);
    setPhoneError('');
    setSubmitError('');
    onClose();
  };
  const handlePhoneChange = (value: string) => {
    setFormData({
      ...formData,
      phone: value
    });
    if (phoneError) {
      setPhoneError('');
    }
  };
  const handlePhoneBlur = () => {
    if (formData.phone && !validatePhoneNumber(formData.phone)) {
      setPhoneError('Please enter a valid phone number starting with + (e.g., +49123456789)');
    }
  };
  if (!isOpen) return null;
  const actualStep = getActualStep(step);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/90 backdrop-blur-sm p-4" onClick={handleClose} data-id="element-119">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.3
    }} className="relative w-full max-w-3xl bg-slate-900 rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-120">
        <button onClick={handleClose} className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white transition-colors z-10" aria-label="Close form" data-id="element-121">
          <XIcon className="w-6 h-6" data-id="element-122" />
        </button>

        <div className="absolute top-0 left-0 right-0 h-1 bg-slate-800" data-id="element-123">
          <motion.div className="h-full bg-gradient-to-r from-orange-500 to-orange-400" initial={{
          width: 0
        }} animate={{
          width: `${step / totalSteps * 100}%`
        }} transition={{
          duration: 0.3
        }} data-id="element-124" />
        </div>

        <div className="p-8 sm:p-12 min-h-[500px] flex flex-col" data-id="element-125">
          <AnimatePresence mode="wait" data-id="element-126">
            {actualStep === 1 && <motion.div key="step1" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} className="flex-1" data-id="element-127">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8" data-id="element-128">
                  What's your English level?
                </h2>

                <div className="grid grid-cols-2 gap-4" data-id="element-129">
                  {englishLevels.map(level => <motion.button key={level.id} onClick={() => setFormData({
                ...formData,
                englishLevel: level.id
              })} className={`flex items-center gap-4 p-5 rounded-xl border transition-all text-left ${formData.englishLevel === level.id ? 'bg-slate-800 border-orange-500/50 text-white' : 'bg-slate-800/50 border-slate-700/50 text-slate-300 hover:border-slate-600'} ${level.id === 'proficient' ? 'col-span-2 sm:col-span-1' : ''}`} whileHover={{
                scale: 1.01
              }} whileTap={{
                scale: 0.99
              }} data-id="element-130">
                      <span className={formData.englishLevel === level.id ? 'text-orange-400' : 'text-slate-500'} data-id="element-131">
                        <TriangleIcon fill={level.fill} data-id="element-132" />
                      </span>
                      <span className="font-semibold" data-id="element-133">{level.label}</span>
                    </motion.button>)}
                </div>
              </motion.div>}

            {actualStep === 2 && <motion.div key="step2" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} className="flex-1" data-id="element-134">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3" data-id="element-135">
                  Do you have basic programming skills in Javascript and Python?
                </h2>
                <p className="text-slate-400 mb-8" data-id="element-136">
                  The majority of your tasks are going to be working on
                  automations and tasks that require logical and clear thinking.
                </p>

                <div className="grid sm:grid-cols-2 gap-4" data-id="element-137">
                  {programmingOptions.map(option => <motion.button key={option.id} onClick={() => setFormData({
                ...formData,
                programmingSkills: option.id
              })} className={`flex items-start gap-4 p-5 rounded-xl border transition-all text-left ${formData.programmingSkills === option.id ? 'bg-slate-800 border-orange-500/50' : 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600'} ${option.id === 'none' ? 'sm:col-span-1' : ''}`} whileHover={{
                scale: 1.01
              }} whileTap={{
                scale: 0.99
              }} data-id="element-138">
                      <span className={`mt-1 ${formData.programmingSkills === option.id ? 'text-orange-400' : 'text-slate-500'}`} data-id="element-139">
                        <option.icon className="w-6 h-6" data-id="element-140" />
                      </span>
                      <div data-id="element-141">
                        <span className="font-semibold text-white block mb-1" data-id="element-142">
                          {option.title}
                        </span>
                        <span className="text-sm text-slate-400" data-id="element-143">
                          {option.description}
                        </span>
                      </div>
                    </motion.button>)}
                </div>
              </motion.div>}

            {actualStep === 3 && <motion.div key="step3" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} className="flex-1" data-id="element-144">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3" data-id="element-145">
                  Are you open to work full time? (40h per week)
                </h2>
                <p className="text-slate-400 mb-8" data-id="element-146">
                  You will be working from home
                </p>

                <div className="grid grid-cols-2 gap-4" data-id="element-147">
                  <motion.button onClick={() => setFormData({
                ...formData,
                fullTime: 'yes'
              })} className={`flex items-center gap-4 p-6 rounded-xl border transition-all ${formData.fullTime === 'yes' ? 'bg-slate-800 border-orange-500/50' : 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600'}`} whileHover={{
                scale: 1.01
              }} whileTap={{
                scale: 0.99
              }} data-id="element-148">
                    <ThumbsUpIcon className={`w-6 h-6 ${formData.fullTime === 'yes' ? 'text-orange-400' : 'text-slate-500'}`} data-id="element-149" />
                    <span className="font-semibold text-white" data-id="element-150">Yes</span>
                  </motion.button>

                  <motion.button onClick={() => setFormData({
                ...formData,
                fullTime: 'no'
              })} className={`flex items-center gap-4 p-6 rounded-xl border transition-all ${formData.fullTime === 'no' ? 'bg-slate-800 border-orange-500/50' : 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600'}`} whileHover={{
                scale: 1.01
              }} whileTap={{
                scale: 0.99
              }} data-id="element-151">
                    <ThumbsDownIcon className={`w-6 h-6 ${formData.fullTime === 'no' ? 'text-orange-400' : 'text-slate-500'}`} data-id="element-152" />
                    <span className="font-semibold text-white" data-id="element-153">No</span>
                  </motion.button>
                </div>
              </motion.div>}

            {actualStep === 4 && <motion.div key="step4" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} className="flex-1" data-id="element-154">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8" data-id="element-155">
                  Please fill in your details so we can get in touch with you!
                </h2>

                <div className="space-y-6" data-id="element-156">
                  <div data-id="element-157">
                    <label className="block text-white font-medium mb-2" data-id="element-158">
                      Your full name
                    </label>
                    <div className="relative" data-id="element-159">
                      <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" data-id="element-160" />
                      <input type="text" value={formData.fullName} onChange={e => setFormData({
                    ...formData,
                    fullName: e.target.value
                  })} placeholder="James Smith" className="w-full pl-12 pr-4 py-4 bg-slate-800/50 border border-slate-700/50 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-orange-500/50 transition-colors" data-id="element-161" />
                    </div>
                  </div>

                  <div data-id="element-162">
                    <label className="block text-white font-medium mb-2" data-id="element-163">
                      Your E-mail
                    </label>
                    <div className="relative" data-id="element-164">
                      <MailIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" data-id="element-165" />
                      <input type="email" value={formData.email} onChange={e => setFormData({
                    ...formData,
                    email: e.target.value
                  })} placeholder="james@smith.com" className="w-full pl-12 pr-4 py-4 bg-slate-800/50 border border-slate-700/50 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-orange-500/50 transition-colors" data-id="element-166" />
                    </div>
                  </div>

                  <div data-id="element-167">
                    <label className="block text-white font-medium mb-2" data-id="element-168">
                      Your Phone Number
                    </label>
                    <div className="relative" data-id="element-169">
                      <PhoneIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" data-id="element-170" />
                      <input type="tel" value={formData.phone} onChange={e => handlePhoneChange(e.target.value)} onBlur={handlePhoneBlur} placeholder="+1 234 567 89" className={`w-full pl-12 pr-4 py-4 bg-slate-800/50 border rounded-xl text-white placeholder-slate-500 focus:outline-none transition-colors ${phoneError ? 'border-red-500/50 focus:border-red-500' : 'border-slate-700/50 focus:border-orange-500/50'}`} data-id="element-171" />
                    </div>
                    {phoneError && <motion.p initial={{
                  opacity: 0,
                  y: -5
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="text-red-400 text-sm mt-2" data-id="element-172">
                        {phoneError}
                      </motion.p>}
                  </div>

                  {submitError && <motion.p initial={{
                opacity: 0,
                y: -5
              }} animate={{
                opacity: 1,
                y: 0
              }} className="text-red-400 text-sm text-center" data-id="element-173">
                      {submitError}
                    </motion.p>}
                </div>
              </motion.div>}

            <div className="flex items-center gap-4 mt-8 pt-6 border-t border-slate-800" data-id="element-174">
              <motion.button onClick={handleBack} className="text-slate-400 hover:text-white font-medium transition-colors" whileHover={{
              x: -2
            }} disabled={isSubmitting} data-id="element-175">
                Back
              </motion.button>

              <motion.button onClick={handleNext} disabled={!canContinue() || isSubmitting} className={`px-8 py-3 rounded-full font-semibold transition-all ${canContinue() && !isSubmitting ? 'bg-gradient-to-r from-orange-500 to-orange-400 text-white shadow-lg shadow-orange-500/25' : 'bg-slate-700 text-slate-400 cursor-not-allowed'}`} whileHover={canContinue() && !isSubmitting ? {
              scale: 1.02
            } : {}} whileTap={canContinue() && !isSubmitting ? {
              scale: 0.98
            } : {}} data-id="element-176">
                {isSubmitting ? 'Submitting...' : step === totalSteps ? 'Send application' : 'Continue'}
              </motion.button>
            </div>
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>;
}